import React from 'react';
import Header from '@/components/Header';
import MinimalFooter from '@/components/MinimalFooter';
import { 
  Headphones, 
  Brain, 
  Sliders, 
  Music, 
  Timer, 
  Volume2,
  Waves,
  Cloud,
  Play,
  Pause,
  Download,
  Maximize,
  Minimize,
  Info,
  ChevronRight,
  PhoneOff,
  Star,
  ListTodo,
  Clock,
  List,
  Wand2,
  Moon,
  Lightbulb
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChatPanel } from '@/components/chat/ChatPanel';

const Help = () => {
  const handleStartTour = () => {
    // This is a no-op since we're on the help page
    console.log('Tour guide not available on help page');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header onStartTour={handleStartTour} />
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-4">Help Center</h1>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Everything you need to know about using BinauralFlow for optimal focus, relaxation, and meditation
          </p>

          {/* Table of Contents */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <List className="h-5 w-5 text-primary" />
                Table of Contents
              </CardTitle>
              <CardDescription>
                Jump to any section of the help guide
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('about')}
                >
                  About BinauralFlow
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('quick-tips')}
                >
                  Quick Tips
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('getting-started')}
                >
                  Getting Started
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('best-practices')}
                >
                  Best Practices
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('frequencies')}
                >
                  Understanding Frequencies
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('presets')}
                >
                  Using Presets
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('custom-sessions')}
                >
                  Custom Sessions
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('ambient-sounds')}
                >
                  Ambient Sounds
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('player-controls')}
                >
                  Player Controls
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => scrollToSection('tips')}
                >
                  Tips & Best Practices
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* About Section */}
          <section id="about" className="mb-12">
            <Card className="bg-card/50 border-primary/10">
              <CardHeader>
                <CardTitle className="text-2xl">About BinauralFlow</CardTitle>
                <CardDescription>
                  Harness the power of binaural beats for focus, relaxation, and enhanced well-being.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  BinauralFlow is your personal sound therapy companion, designed to help you achieve
                  specific mental states through the use of binaural beats. Whether you're looking to
                  boost your focus, deepen your meditation practice, or simply unwind after a long day,
                  BinauralFlow offers a curated selection of sound frequencies to guide you.
                </p>
                <div className="grid gap-6">
                  <div className="flex items-start space-x-4">
                    <div className="rounded-lg bg-primary/10 p-3">
                      <Wand2 className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">Focus and Productivity</p>
                      <p className="text-sm text-muted-foreground">
                        Beta waves to enhance focus and mental clarity.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="rounded-lg bg-primary/10 p-3">
                      <Moon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">Deep Meditation</p>
                      <p className="text-sm text-muted-foreground">
                        Theta waves to deepen your meditation practice.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="rounded-lg bg-primary/10 p-3">
                      <Lightbulb className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium leading-none">Enhanced Learning</p>
                      <p className="text-sm text-muted-foreground">
                        Alpha waves to improve learning and memory retention.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Quick Tips Card */}
          <Card id="quick-tips" className="mb-12">
            <CardHeader>
              <CardTitle className="text-lg">Quick Tips</CardTitle>
              <CardDescription>Essential information for beginners</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Headphones className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Use Headphones</h4>
                  <p className="text-sm text-muted-foreground">For proper binaural effect, headphones are essential</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Start with 15-20 minutes</h4>
                  <p className="text-sm text-muted-foreground">Beginners should limit sessions to 15-20 minutes</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Volume2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Moderate Volume</h4>
                  <p className="text-sm text-muted-foreground">Keep volume at a comfortable level, not too loud</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Getting Started Section */}
          <section id="getting-started" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Getting Started
            </h2>
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">1</div>
                  <h3 className="text-xl font-medium">Choose a Preset</h3>
                </div>
                <p className="pl-8 text-muted-foreground">
                  Select one of our carefully designed presets based on your current needs - Focus, Meditation, Relaxation, or Sleep.
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">2</div>
                  <h3 className="text-xl font-medium">Put on Headphones</h3>
                </div>
                <p className="pl-8 text-muted-foreground">
                  For binaural beats to work properly, you must use stereo headphones or earbuds. The effect is created when each ear receives a slightly different frequency.
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">3</div>
                  <h3 className="text-xl font-medium">Adjust Volume</h3>
                </div>
                <p className="pl-8 text-muted-foreground">
                  Set the volume to a comfortable level - not too loud, but audible enough to perceive the beats. You can also mix in ambient sounds if desired.
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">4</div>
                  <h3 className="text-xl font-medium">Start Your Session</h3>
                </div>
                <p className="pl-8 text-muted-foreground">
                  Press play and find a comfortable position. Close your eyes and allow the beats to guide your brainwave activity. For beginners, start with 15-20 minute sessions.
                </p>
              </div>
            </div>
          </section>

          {/* Best Practices Section */}
          <section id="best-practices" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PhoneOff className="h-5 w-5 text-primary" />
                    Minimize Distractions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Find a quiet place where you won't be disturbed. Put your phone on silent mode and let others know you need uninterrupted time.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-primary" />
                    Consistency is Key
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    For best results, use binaural beats regularly. Many users report that daily sessions yield the most noticeable benefits.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Waves className="h-5 w-5 text-primary" />
                    Experiment with Frequencies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Different frequencies affect people differently. Don't be afraid to experiment with various presets and custom frequencies to find what works best for you.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListTodo className="h-5 w-5 text-primary" />
                    Track Your Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Keep notes on which frequencies and durations work best for different purposes. Save your favorite sessions for easy access later.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Understanding Frequencies Section */}
          <section id="frequencies" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Understanding Frequencies
            </h2>
            <div className="glass rounded-2xl p-6 animate-fade-in">
              <div className="flex items-center gap-2 mb-4">
                <Brain size={18} className="text-primary" />
                <h3 className="text-lg font-medium">Scientific Information</h3>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Learn more about binaural beats and how different frequencies affect your brain state.
              </p>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="delta">
                  <AccordionTrigger className="text-sm font-medium">Delta Waves (0.5-4 Hz)</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Delta waves are the slowest recorded brain waves in humans and are most present during deep, dreamless sleep.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Associated with deepest meditation and restorative sleep</li>
                      <li>Healing and regeneration are stimulated in this state</li>
                      <li>Reduces cortisol and enhances DHEA for anti-aging effects</li>
                      <li>Access to unconscious mind and collective unconscious</li>
                      <li>Ideal for: Deep healing sleep, recovery from illness</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="theta">
                  <AccordionTrigger className="text-sm font-medium">Theta Waves (4-8 Hz)</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Theta brainwaves occur most often in sleep but are also dominant in deep meditation and light sleep, including the REM dream state.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Gateway to learning, memory, and intuition</li>
                      <li>Heightened receptivity and inspiration</li>
                      <li>Vivid visualizations and increased creativity</li>
                      <li>Deep emotional connections and insights</li>
                      <li>Ideal for: Deep meditation, creative problem solving</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="alpha">
                  <AccordionTrigger className="text-sm font-medium">Alpha Waves (8-13 Hz)</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Alpha brainwaves are dominant during quietly flowing thoughts and in some meditative states, representing the bridge between conscious thinking and subconscious mind.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Relaxed alertness, calm and centered</li>
                      <li>Reduced anxiety and stress</li>
                      <li>Positive thinking and emotional stability</li>
                      <li>Enhanced learning and memory recall</li>
                      <li>Ideal for: Relaxation, stress reduction, light meditation</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="beta">
                  <AccordionTrigger className="text-sm font-medium">Beta Waves (13-30 Hz)</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Beta brainwaves dominate our normal waking state of consciousness when attention is directed towards cognitive tasks and the outside world.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Active, busy, or anxious thinking</li>
                      <li>Focus, concentration and alertness</li>
                      <li>Problem-solving and decision making</li>
                      <li>Enhanced cognitive performance</li>
                      <li>Ideal for: Work, study, analysis, active conversation</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="gamma">
                  <AccordionTrigger className="text-sm font-medium">Gamma Waves (30-100 Hz)</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Gamma brainwaves are the fastest of brain waves and relate to simultaneous processing of information from different brain areas, associated with higher states of conscious perception.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Higher mental activity and perception</li>
                      <li>Peak concentration and high-level information processing</li>
                      <li>Associated with expanded consciousness states</li>
                      <li>Present during bursts of insight and high-level cognitive functioning</li>
                      <li>Ideal for: Advanced cognitive enhancement, high-level processing</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="research">
                  <AccordionTrigger className="text-sm font-medium">Research & Studies</AccordionTrigger>
                  <AccordionContent className="text-sm">
                    <p className="mb-2">
                      Scientific research on binaural beats has shown promising results in several areas:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>
                        <strong>Memory Enhancement:</strong> Research published in the Journal of Cognitive Enhancement found theta binaural beats improved working memory performance.
                      </li>
                      <li>
                        <strong>Anxiety Reduction:</strong> A 2019 study in the journal "Psychological Research" showed alpha binaural beats significantly reduced pre-operative anxiety.
                      </li>
                      <li>
                        <strong>Sleep Quality:</strong> Research in "Frontiers in Human Neuroscience" found delta binaural beats improved deep sleep quality and increased sleep time.
                      </li>
                      <li>
                        <strong>Attention:</strong> Beta frequency binaural beats were shown to enhance attention in a study published in "Psychological Research" (2017).
                      </li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              
              <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground bg-primary/5 p-3 rounded-lg">
                <Info size={14} className="text-primary mt-0.5" />
                <p>
                  Note: While binaural beats have shown promising results in research, individual experiences may vary. 
                  Consult with a healthcare professional before using binaural beats if you have neurological conditions 
                  or epilepsy.
                </p>
              </div>
            </div>
          </section>

          {/* Presets Section */}
          <section id="presets" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Using Presets
            </h2>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5 text-primary" />
                  Quick Start with Presets
                </CardTitle>
                <CardDescription>
                  Get started quickly with our carefully curated presets
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="font-medium">How to Use Presets</h4>
                    <ol className="text-sm text-muted-foreground space-y-2">
                      <li>1. Browse the preset collection</li>
                      <li>2. Click on a preset to select it</li>
                      <li>3. Adjust volume if needed</li>
                      <li>4. Click play to start your session</li>
                    </ol>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Preset Features</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Pre-configured frequencies</li>
                      <li>• Optimized durations</li>
                      <li>• Curated ambient sounds</li>
                      <li>• Save favorites for quick access</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Custom Sessions Section */}
          <section id="custom-sessions" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Custom Sessions
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sliders className="h-5 w-5 text-primary" />
                    Basic Session Designer
                  </CardTitle>
                  <CardDescription>
                    Create simple custom sessions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Features</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Adjust frequency and duration</li>
                      <li>• Mix ambient sounds</li>
                      <li>• Save custom sessions</li>
                      <li>• Download sessions</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    Advanced Mode
                  </CardTitle>
                  <CardDescription>
                    Fine-tune every aspect of your session
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Advanced Features</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Custom carrier frequencies</li>
                      <li>• Multiple waveform options</li>
                      <li>• Multi-stage sessions</li>
                      <li>• Progressive frequency changes</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Ambient Sounds Section */}
          <section id="ambient-sounds" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Ambient Sounds
            </h2>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Waves className="h-5 w-5 text-primary" />
                  Mixing Ambient Sounds
                </CardTitle>
                <CardDescription>
                  Enhance your session with natural soundscapes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="font-medium">Available Sounds</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Rain and thunder</li>
                      <li>• Ocean waves</li>
                      <li>• Forest ambience</li>
                      <li>• White noise</li>
                      <li>• Meditation bells</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Tips</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Start with low volumes</li>
                      <li>• Mix 2-3 sounds maximum</li>
                      <li>• Adjust levels for balance</li>
                      <li>• Save favorite combinations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Player Controls Section */}
          <section id="player-controls" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Player Controls
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-5 w-5 text-primary" />
                    Basic Controls
                  </CardTitle>
                  <CardDescription>
                    Essential playback controls
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Available Controls</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Play/Pause</li>
                      <li>• Volume adjustment</li>
                      <li>• Master volume</li>
                      <li>• Individual sound volumes</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Maximize className="h-5 w-5 text-primary" />
                    Advanced Features
                  </CardTitle>
                  <CardDescription>
                    Additional player features
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Features</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Focus mode (fullscreen)</li>
                      <li>• Session recording</li>
                      <li>• Download sessions</li>
                      <li>• Session history</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Tips & Best Practices */}
          <section id="tips" className="mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <ChevronRight className="h-6 w-6 text-primary" />
              Tips & Best Practices
            </h2>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  Getting the Most Out of BinauralFlow
                </CardTitle>
                <CardDescription>
                  Maximize your experience with these tips
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="font-medium">Session Tips</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Start with shorter sessions</li>
                      <li>• Use appropriate frequencies for your goal</li>
                      <li>• Create a comfortable environment</li>
                      <li>• Stay hydrated during longer sessions</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Technical Tips</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>• Keep your browser updated</li>
                      <li>• Use a stable internet connection</li>
                      <li>• Allow audio permissions</li>
                      <li>• Clear cache if experiencing issues</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
      <ChatPanel />
      <MinimalFooter />
    </div>
  );
};

export default Help; 